import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  LocalShipping as LocalShippingIcon,
} from "@mui/icons-material";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface OrderHeaderProps {
  orderNumber: string;
  createdAt: string;
  updatedAt?: string;
  status: string;
  paymentStatus: string;
  cargoCompanyName?: string;
  cargoCompanyId?: string;
  onEditOrder: () => void;
  onAssignCargoCompany?: () => void;
}

const OrderHeader: React.FC<OrderHeaderProps> = ({
  orderNumber,
  createdAt,
  updatedAt,
  status,
  paymentStatus,
  cargoCompanyName,
  cargoCompanyId,
  onEditOrder,
  onAssignCargoCompany,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Delivered":
        return "success";
      case "Shipped":
        return "primary";
      case "Processing":
        return "warning";
      case "Cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    return status === "Paid" ? "success" : "warning";
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/admin/orders");
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Tooltip title={t("admin.orders.backToOrders")}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h4" component="h1">
            {t("admin.orders.orderNumberTitle", { number: orderNumber })}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {onAssignCargoCompany && (
            <Tooltip title={t("admin.orders.assignCargoCompany")}>
              <IconButton onClick={onAssignCargoCompany} color="primary">
                <LocalShippingIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t("admin.orders.editOrder")}>
            <IconButton onClick={onEditOrder} color="primary">
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t("admin.orders.created")}: {new Date(createdAt).toLocaleDateString()}
        </Typography>
        {updatedAt && (
          <Typography variant="body2" color="text.secondary">
            {t("admin.orders.updated")}: {new Date(updatedAt).toLocaleDateString()}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Chip
          label={status}
          color={getStatusColor(status) as any}
          variant="outlined"
          size="small"
        />
        <Chip
          label={paymentStatus}
          color={getPaymentStatusColor(paymentStatus) as any}
          variant="outlined"
          size="small"
        />
        {cargoCompanyName && (
          <Chip
            label={t("admin.orders.cargoCompany", { name: cargoCompanyName })}
            color="default"
            variant="outlined"
            size="small"
            onClick={() =>
              cargoCompanyId &&
              navigate(`/admin/cargo-companies/${cargoCompanyId}/users`)
            }
            sx={{
              cursor: cargoCompanyId ? "pointer" : "default",
              "&:hover": cargoCompanyId ? { opacity: 0.8 } : {},
            }}
          />
        )}
        {!cargoCompanyName && (
          <Chip
            label={t("admin.orders.cargoUnassigned")}
            color="default"
            variant="outlined"
            size="small"
            sx={{ opacity: 0.6 }}
          />
        )}
      </Box>
    </Box>
  );
};

export default OrderHeader;
